import { Box, IconButton } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { toast } from 'material-react-toastify';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { ProductImage } from '../../interfaces/Product/ProductImage';
import { useThemeColor } from '../../hooks/useThemeColor';
import { useIsMobile } from '../../hooks/useIsMobile';
import { extractYoutubeVideoID } from '../../utils/Youtube';
import { YoutubeSummary } from '../../interfaces/Youtube/Youtube';
import { getYoutubeSummaryByVideoID } from '../../api/Youtube';
import { YoutubePreviewDialog } from '../YoutubePreviewDialog/YoutubePreviewDialog';

const carouselImageWidth = 100;
const carouselImageHeight = 120;
const carouselImageMargin = 10;

type CarouselProps = {
  images: ProductImage[];
  onHover: (index: number) => void;
};

const Carousel = ({ images, onHover }: CarouselProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const isMobile = useIsMobile();

  const { primary, secondary } = useThemeColor();

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  useEffect(() => {
    setActiveIndex(0);
  }, [images]);

  const itemsVisible = Math.floor(
    containerWidth / (carouselImageWidth + carouselImageMargin),
  );
  const maxStartIndex = Math.max(images.length - itemsVisible, 0);

  const handleNext = useCallback(() => {
    setActiveIndex((prev) => Math.min(prev + 1, maxStartIndex));
  }, [maxStartIndex]);

  const handlePrev = useCallback(() => {
    setActiveIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        overflow: isMobile ? 'auto' : 'hidden',
        position: 'relative',
      }}
    >
      {!isMobile && (
        <>
          <IconButton
            onClick={handlePrev}
            disabled={activeIndex === 0}
            sx={{
              position: 'absolute',
              top: '50%',
              left: 0,
              transform: 'translateY(-50%)',
              zIndex: 2,
              backgroundColor: primary,
              color: secondary,
              '&:hover': {
                backgroundColor: primary,
                opacity: 0.8,
              },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>

          <IconButton
            onClick={handleNext}
            disabled={activeIndex >= maxStartIndex}
            sx={{
              position: 'absolute',
              top: '50%',
              right: 0,
              transform: 'translateY(-50%)',
              zIndex: 2,
              backgroundColor: primary,
              color: secondary,
              '&:hover': {
                backgroundColor: primary,
                opacity: 0.8,
              },
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </>
      )}

      <Box
        sx={{
          width: `${
            (carouselImageWidth + carouselImageMargin) * images.length
          }px`,
          display: 'flex',
          flexDirection: 'row',
          transform: `translateX(-${
            (carouselImageWidth + carouselImageMargin) * activeIndex
          }px)`,
          transition: 'transform 0.5s',
          gap: `${carouselImageMargin}px`,
        }}
      >
        {images.map((image, i) => (
          <Box
            key={i}
            width={carouselImageWidth}
            height={carouselImageHeight}
            position='relative'
            overflow='hidden'
            onMouseEnter={() => onHover(i)}
            sx={{
              cursor: 'pointer',
            }}
          >
            <Box
              component='img'
              src={image.path}
              width='100%'
              height='auto'
              display='block'
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

type ProductGalleryProps = {
  images: ProductImage[];
  videoURL?: string;
};

export const ProductGallery = ({ images, videoURL }: ProductGalleryProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [youtubeSummary, setYoutubeSummary] = useState<
    YoutubeSummary | undefined
  >(undefined);
  const [showYoutubeDialog, setShowYoutubeDialog] = useState(false);

  const resolvedImages = useMemo(
    () => [
      ...images,
      ...(youtubeSummary ? [{ path: youtubeSummary.thumbnail }] : []),
    ],
    [images, youtubeSummary],
  );

  const videoID = useMemo(
    () => (videoURL ? extractYoutubeVideoID(videoURL) : undefined),
    [videoURL],
  );

  useEffect(() => {
    setSelectedIndex(0);
  }, [images]);

  useEffect(() => {
    setYoutubeSummary(undefined);
    if (!videoID) return;
    getYoutubeSummaryByVideoID(videoID)
      .then(setYoutubeSummary)
      .catch((err) => toast.error(err.message));
  }, [videoID]);

  const isVideoSelected = selectedIndex >= images.length;

  const onClick = useCallback(() => {
    if (isVideoSelected) {
      setShowYoutubeDialog(true);
    }
  }, [isVideoSelected]);

  return (
    <Box
      sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2.5 }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          paddingBottom: '100%',
          overflow: 'hidden',
          border: '1px solid #e1e1e1',
        }}
      >
        {!!videoID && (
          <YoutubePreviewDialog
            title={youtubeSummary?.title}
            videoID={videoID}
            open={showYoutubeDialog}
            onClose={() => setShowYoutubeDialog(false)}
          />
        )}

        {isVideoSelected && (
          <IconButton
            onClick={onClick}
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 3,
              padding: 0, // optional: remove extra padding
            }}
          >
            <YouTubeIcon sx={{ fontSize: 80, color: '#FF0000' }} />
          </IconButton>
        )}

        <Box
          component='img'
          src={
            isVideoSelected
              ? youtubeSummary?.thumbnail
              : images[selectedIndex]?.path
          }
          width='100%'
          height='100%'
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            objectFit: 'contain',
          }}
        />
      </Box>

      <Carousel images={resolvedImages} onHover={setSelectedIndex} />
    </Box>
  );
};
